import dayjs from "dayjs"

import {
  BLANK_PRODUCT_TEST_DOCUMENT,
  PRODUCT_TEST_DOCUMENT_TYPE_TO_COLOR,
  PRODUCT_TEST_DOCUMENT_TYPE_TO_LABEL,
  PRODUCT_TEST_RESULT_PASS_FAIL_TO_LABEL,
  PRODUCT_TEST_RESULT_YES_NO_TO_LABEL,
  PRODUCT_TEST_STATUS_TO_LABEL,
} from "./constants"
import type { ProductTestFormValues } from "./schemas"
import type {
  ProductTestFormPayload,
  ProductTestResultPassFail,
  ProductTestResultYesNo,
  ProductTestWithDocuments,
} from "./types"
import { ProductTestDiffType } from "./types"

import type {
  AssociatedTestTemplate,
  FieldGroupEnum,
  ProductTest,
  ProductTestStatus,
  SignedUrl,
} from "@/graphql/codegen/graphql"
import colors from "@/styles/colors"
import { toNumberOrNull, toStringOrEmpty, toStringOrNull } from "@/utils/forms"

export const getProductTestStatusLabel = (status?: ProductTestStatus | null): string => {
  if (!status) return ""

  return PRODUCT_TEST_STATUS_TO_LABEL.get(status) ?? status
}

export const getProductTestDocumentTypeLabel = (documentType?: FieldGroupEnum | null): string => {
  if (!documentType) return ""

  return PRODUCT_TEST_DOCUMENT_TYPE_TO_LABEL.get(documentType) ?? documentType
}

export const getProductTestDocumentTypeColor = (documentType?: FieldGroupEnum | null): string => {
  if (!documentType) return "transparent"

  return PRODUCT_TEST_DOCUMENT_TYPE_TO_COLOR.get(documentType) ?? documentType
}

export const getProductTestResultPassFailLabel = (value?: ProductTestResultPassFail | null): string => {
  if (!value) return ""

  return PRODUCT_TEST_RESULT_PASS_FAIL_TO_LABEL.get(value) ?? value
}

export const getProductTestResultYesNoLabel = (value?: ProductTestResultYesNo | null): string => {
  if (!value) return ""

  return PRODUCT_TEST_RESULT_YES_NO_TO_LABEL.get(value) ?? value
}

export const getShelfLifePercentage = (expirationDate: string, manufacturingDate: string): number => {
  const today = dayjs()
  const expiresOn = dayjs(expirationDate)
  const manufacturedOn = dayjs(manufacturingDate)

  const totalShelfLife = expiresOn.diff(manufacturedOn)
  const remainingShelfLife = expiresOn.diff(today)
  return (remainingShelfLife / totalShelfLife) * 100
}

export const getProductTestResultShelfLifeColor = (expirationDate: string, manufacturingDate: string): string => {
  const shelfLifePercentage = getShelfLifePercentage(expirationDate, manufacturingDate)

  if (shelfLifePercentage <= 0) {
    return colors.secondary["100"] // Red: Expired (≤ 0%)
  } else if (shelfLifePercentage <= 50) {
    return colors.tertiary.yellow["100"] // Amber: ≤ 50% and > 0%
  } else {
    return colors.tertiary.green["100"] // Green: > 50%
  }
}

export const getProductTestDefaultFormValues = (
  associatedTestTemplates: AssociatedTestTemplate[],
  productTest?: ProductTestWithDocuments | null
): ProductTestFormValues => {
  const defaultResults = associatedTestTemplates.map((associatedTemplate) => ({
    associatedTestTemplateId: associatedTemplate.associatedTestTemplateId,
    value: "",
    printToCoa: associatedTemplate.printToAkrochem,
  }))

  const lotNumber = productTest ? [productTest?.lotNumber, productTest?.version].filter(Boolean).join(" ").trim() : ""

  return {
    lotNumber,
    batchNumber: toStringOrEmpty(productTest?.batchNumber),
    orderId: "",
    manufacturingDate: toStringOrEmpty(productTest?.manufacturingDate),
    expirationDate: toStringOrEmpty(productTest?.expirationDate),
    status: toStringOrEmpty(productTest?.status),
    results:
      productTest?.results?.map((result) => ({
        productTestResultId: result.productTestResultId,
        associatedTestTemplateId: result.associatedTestTemplate!.associatedTestTemplateId,
        value: toStringOrEmpty(result.value),
        printToCoa: result.printToCoa!,
      })) ?? defaultResults,
    comment: "",
    documents: productTest?.documents.length ? [] : [BLANK_PRODUCT_TEST_DOCUMENT],
  }
}

export const compareProductTestValues = (
  productTest: ProductTest,
  formValues: ProductTestFormValues
): ProductTestDiffType | null => {
  if (productTest.results?.some((result, index) => result.value !== formValues.results[index].value)) {
    return ProductTestDiffType.TestResults
  }

  if (productTest.expirationDate !== formValues.expirationDate) {
    return ProductTestDiffType.ExpirationDate
  }

  return null
}

export const hasProductTestDocumentsChanged = (
  existingDocuments: SignedUrl[],
  currentDocuments: SignedUrl[]
): boolean => {
  if (existingDocuments.length !== currentDocuments.length) return true

  return !existingDocuments.every((existingDocument) =>
    currentDocuments.some((currentDocument) => existingDocument.file?.fileId === currentDocument.file?.fileId)
  )
}

export const hasProductTestPoIdsChanged = (existingPoIds: number[], currentPoIds: number[]): boolean => {
  if (existingPoIds.length !== currentPoIds.length) return true
  return !existingPoIds.every((poId) => currentPoIds.includes(poId))
}

export const serializeProductTestForm = (
  formValues: ProductTestFormValues,
  productTest?: ProductTestWithDocuments | null,
  updatedProductTestDocuments: SignedUrl[] = [],
  productTestPoIds: number[] = [],
  isResultsChanged = false
): ProductTestFormPayload => {
  const documentsToCreate = formValues.documents.filter(({ file }) => file)
  let documentsToDelete: string[] = []

  if (productTest) {
    const updatedFileIds = new Set(updatedProductTestDocuments.map((doc) => doc.file?.fileId).filter(Boolean))
    documentsToDelete = productTest.documents
      .filter((document) => !updatedFileIds.has(document.file?.fileId))
      .map((document) => document.file?.fileId ?? "")
      .filter(Boolean)
  }

  // Convert single order ID string to array with one number (for now, hardcoded to use single order ID)
  const orderId = toNumberOrNull(formValues.orderId)
  const isNewOrderIdAdded = !productTestPoIds.includes(orderId!)
  const orderIds = isNewOrderIdAdded && orderId ? [orderId].concat(productTestPoIds) : productTestPoIds

  return {
    productTest: {
      lotNumber: formValues.lotNumber,
      batchNumber: toStringOrNull(formValues.batchNumber),
      orderIds,
      manufacturingDate: formValues.manufacturingDate,
      expirationDate: formValues.expirationDate,
      status: toStringOrNull(formValues.status) as ProductTestStatus | null,
      // Send results only if they have changed in the form
      results: isResultsChanged ? formValues.results : null,
      comment: toStringOrNull(formValues.comment),
    },
    documentsToCreate,
    documentsToDelete,
  }
}

export const isTestResultsValid = (
  { equalToValue, minimumValue, maximumValue }: AssociatedTestTemplate,
  testResultsValue: number
): boolean => {
  return !(
    (minimumValue && testResultsValue < +minimumValue) ||
    (maximumValue && testResultsValue > +maximumValue) ||
    (equalToValue && testResultsValue !== +equalToValue)
  )
}
